import * as React from "react"
import { keepStill } from "./still"
import { Sections } from "../work/sections"
import { ABOUT } from "./content"
import { progress } from "../home/motion"
import { IntroText } from "./About"
import { EDGE, FPS } from "./bar"
import Constellation, { type ConstellationApi, TEXT_AT, BUILD_END } from "./Constellation"
import AwardVideo, { type AwardApi } from "./AwardVideo"
import "./about.css"

/** /aabout: a full-screen video (Tiago, the light on him going dark from the
 *  bottom up), played by the scroll. It fades in as the photo did, the intro
 *  comes in line by line as on the home, and each line of the intro fades as
 *  the dark rises to it. The page is tall; the video stays on screen. */

/** the constellation starts building once the dark has risen past the middle
 *  (the intro gone): this far into the video (s) */
const CONSTELLATION_AT = Math.max(0, EDGE.findIndex((e, i) => i > 0 && e >= 0 && e < 0.45)) / FPS
/** The scroll, start to end (fractions of it): the video plays over the first
 *  part (the same distance as ever); the constellation builds from when the dark
 *  passes the middle, holds, then becomes the frame of logos, which hold to the
 *  end. All of it follows the scroll, so none of it can be passed by. */
// (in screen heights of scroll: the video 3.11, the texts 0.9 after the dark,
// a hold of 0.5, the change to the logos 1.2, 0.5 on the logos, the award
// coming in 0.5 and turning over 2, then 0.2: 8.42 in all)
// (phones: more room again for the award coming in; the page's height in
// about.css follows: 100vh + TOTAL at 0.685 screens each)
const TOUCH = typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches
const MORE = TOUCH ? 0.6 : 0
const TOTAL = 12.82 + MORE
const VIDEO_PART = 3.11 / TOTAL
/** the dots come out while the dark rises; the texts, lines and futurism once
 *  it's all dark (the light gone from the top: on phones the texts reach up
 *  there) */
const DARK = Math.max(0, EDGE.findIndex((e, i) => i > 0 && e >= 0 && e < 0.03)) / FPS
const BUILT_AT = 3.52 / TOTAL
const SWAP_FROM = 4.02 / TOTAL
const SWAP_TO = 5.22 / TOTAL
/** the award: in (the logos going), then turning */
const AWARD_FROM = 5.72 / TOTAL
const AWARD_IN = (6.82 + MORE) / TOTAL
const AWARD_TURNED = (8.82 + MORE) / TOTAL
/** the end: to black, then the contact and the circle drawn (3.6 screens), then held 0.3 */
const END_FROM = (8.92 + MORE) / TOTAL
const END_IN = (12.52 + MORE) / TOTAL
/** how far into its coming in the award starts to show (the logos gone by then) */
const AWARD_SHOWS = 0.48 // (Duvel, the last logo, 85% gone)

// a line fades over this distance (px) of the dark's edge rising to its middle
const FADE = 56

/** where the dark's edge is at time t (s): 0..1 of the frame's height */
function edgeAt(t: number) {
    const f = Math.max(0, Math.min(EDGE.length - 1, t * FPS))
    const i = Math.floor(f),
        k = f - i
    const a = EDGE[i],
        b = EDGE[Math.min(EDGE.length - 1, i + 1)]
    // (coming in or going out: no in-between)
    if (a < 0 || b < 0 || a > 1 || b > 1) return k < 0.5 ? a : b
    return a + (b - a) * k
}

// The video, fetched once for the page however often it's opened: started
// early (the menu opening, a pointer on a link to here) so it's in by arrival.
const darkSize = () => (innerWidth / innerHeight < 0.9 ? "720" : "1920")
function darkFiles(src: string) {
    const files = [`/about/dark-${src}.mp4`, `/about/dark-${src}.webm`]
    const v = document.createElement("video")
    if (v.canPlayType('video/mp4; codecs="avc1.640028"') !== "probably" && v.canPlayType('video/webm; codecs="vp9"')) files.reverse()
    return files
}
const fetched: Record<string, Promise<Blob>> = {}
function fetchOnce(file: string) {
    return (fetched[file] ||= fetch(file)
        .then((r) => (r.ok ? r.blob() : Promise.reject()))
        .catch((e) => {
            delete fetched[file]
            throw e
        }))
}
/** Start loading the about page: its first picture and its video. */
export function preloadAbout() {
    const src = darkSize()
    new Image().src = `/about/dark-poster-${src}.jpg`
    fetchOnce(darkFiles(src)[0]).catch(() => {})
}

export default function AboutVideo() {
    const root = React.useRef<HTMLDivElement>(null)
    const stage = React.useRef<HTMLDivElement>(null)
    const video = React.useRef<HTMLVideoElement>(null)
    const intro = React.useRef<HTMLDivElement>(null)
    const cn = React.useRef<ConstellationApi | null>(null)
    const aw = React.useRef<AwardApi | null>(null)
    // the video can follow the scroll (it's in memory, every frame reachable):
    // until then the page waits at its start with it, so nothing runs ahead
    const scrubs = React.useRef(false)
    // phones (portrait): the video's centre, cropped (no pixels they never show)
    const src = React.useMemo(darkSize, [])

    // The video comes in once its first frame is ready: fading in while settling
    // from a little larger (as the photo did, about.css).
    const ready = React.useRef<Promise<void>>(null as any)
    if (!ready.current) {
        let done: () => void
        ready.current = new Promise<void>((r) => (done = r))
        ;(ready.current as any).done = () => done()
    }
    React.useLayoutEffect(() => {
        const v = video.current!
        const show = () => {
            v.setAttribute("data-shown", "")
            ;(ready.current as any).done()
        }
        if (v.readyState >= 2) show()
        else v.addEventListener("loadeddata", show, { once: true })
        // (it only ever plays for a moment, for iOS, which draws a video's frames
        // only once it has played; any other playing is stopped at once, and it
        // goes back to where the scroll is: still.ts)
        const still = keepStill(v)
        still.prime()
        // never later than this: the poster is there already
        const late = setTimeout(show, 1500)
        // To scrub, the video has to be seekable: a server that won't send parts
        // of the file (Cloudflare Pages, at times) leaves it stuck on its first
        // frame. So the whole file is fetched once and played from memory, where
        // every frame can be reached; until then the file plays as streamed.
        let url = "",
            gone = false
        // Fetched at once: the format this browser plays (H.264, else the WebM),
        // the other if it turns out it can't.
        const files = darkFiles(src)
        const load = (k: number) => {
            if (k >= files.length || gone) return
            fetchOnce(files[k])
                .then((b) => {
                    if (gone) return
                    if (url) URL.revokeObjectURL(url)
                    url = URL.createObjectURL(b)
                    const onMeta = () => {
                        clean()
                        scrubs.current = true
                        v.dispatchEvent(new Event("scrubs"))
                    }
                    const onError = () => (clean(), load(k + 1))
                    const clean = () => (v.removeEventListener("loadedmetadata", onMeta), v.removeEventListener("error", onError))
                    v.addEventListener("loadedmetadata", onMeta)
                    v.addEventListener("error", onError)
                    v.src = url
                    v.load()
                    still.prime()
                })
                .catch(() => load(k + 1))
        }
        load(0)
        return () => {
            gone = true
            still.release()
            clearTimeout(late)
            if (url) URL.revokeObjectURL(url)
        }
    }, [])

    // the lines of the intro (however the text wraps), and where each is on screen
    const lines = React.useRef<{ words: HTMLElement[]; top: number; mid: number }[]>([])
    const measure = React.useCallback(() => {
        const ws = Array.from(intro.current!.querySelectorAll<HTMLElement>(".ab-w"))
        const out: { words: HTMLElement[]; top: number; mid: number }[] = []
        let top = -Infinity
        for (const w of ws) {
            // (the version not shown has no lines)
            if (!w.offsetParent) continue
            const r = w.getBoundingClientRect()
            if (Math.abs(r.top - top) > 2) out.push({ words: [], top: (top = r.top), mid: r.top + r.height / 2 })
            out[out.length - 1].words.push(w)
        }
        lines.current = out
    }, [])

    // The intro comes in line by line, as the headline on the home: each line
    // rises from its own height and fades in, 400ms, 90ms after the one before,
    // once the video and the font are ready (at most 0.6s).
    React.useLayoutEffect(() => {
        measure()
        const words = lines.current.flatMap((l, n) => l.words.map((w) => ({ el: w.firstElementChild as HTMLElement, n })))
        const draw = (t: number) =>
            words.forEach(({ el, n }) => {
                const k = progress(t, 90 * n, 400, "out")
                el.style.transform = k < 1 ? `translateY(${100 * (1 - k)}%)` : ""
                el.style.opacity = String(k)
            })
        const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches
        draw(reduced ? 1e9 : 0)
        let raf = 0,
            gone = false
        if (!reduced)
            Promise.race([Promise.all([ready.current, (document as any).fonts?.ready]), new Promise((r) => setTimeout(r, 600))]).then(() => {
                if (gone) return
                let t0 = 0
                const end = 90 * lines.current.length + 400
                const step = (now: number) => {
                    if (!t0) t0 = now
                    draw(now - t0)
                    if (now - t0 < end) raf = requestAnimationFrame(step)
                }
                raf = requestAnimationFrame(step)
            })
        return () => {
            gone = true
            cancelAnimationFrame(raf)
        }
    }, [])

    // The scroll plays the video: its position is the time. Each line of the
    // intro goes just before the bar's lower edge reaches it.
    React.useLayoutEffect(() => {
        const v = video.current!
        let duration = EDGE.length / FPS
        const onMeta = () => isFinite(v.duration) && v.duration > 0 && (duration = v.duration)
        v.addEventListener("loadedmetadata", onMeta)
        onMeta()
        // (one seek at a time: the next once the last is drawn)
        let pending: number | null = null
        const seek = (t: number) => {
            if (v.seeking) return void (pending = t)
            if (Math.abs(v.currentTime - t) > 0.001) v.currentTime = t
        }
        // the header over the video: white, blended by difference (about.css)
        const html = document.documentElement
        html.setAttribute("data-av", "")
        const release = () => (html.removeAttribute("data-av"), html.removeAttribute("data-av-end"))
        const onSeeked = () => {
            if (pending == null) return
            const t = pending
            pending = null
            seek(t)
        }
        v.addEventListener("seeked", onSeeked)
        const clamp = (v: number) => Math.max(0, Math.min(1, v))
        // `at`: where the scroll is (0..1 of it)
        const paint = (at: number) => {
            // (held at the start until the video can follow)
            const f = scrubs.current ? at : 0
            const t = Math.min(1, f / VIDEO_PART) * duration
            // the constellation and the logos, as far as the scroll has come
            const from = (CONSTELLATION_AT / duration) * VIDEO_PART
            const dark = (DARK / duration) * VIDEO_PART
            // (the build in two: the dots up to the dark, the rest after)
            const build = f < dark ? clamp((f - from) / (dark - from)) * (TEXT_AT / BUILD_END) : (TEXT_AT + clamp((f - dark) / (BUILT_AT - dark)) * (BUILD_END - TEXT_AT)) / BUILD_END
            const award = clamp((f - AWARD_FROM) / (AWARD_IN - AWARD_FROM))
            const end = clamp((f - END_FROM) / (END_IN - END_FROM))
            // (at the end, all black: the header plainly white, over the circle being drawn, not blended with it)
            html.toggleAttribute("data-av-end", end > 0)
            cn.current?.set(build, clamp((f - SWAP_FROM) / (SWAP_TO - SWAP_FROM)), (f - from) / (SWAP_FROM - from), award, end)
            // (the award once the logos are gone: the last fading as it starts)
            const shown = AWARD_FROM + AWARD_SHOWS * (AWARD_IN - AWARD_FROM)
            // (and at the end to black: on into its own fade, then gone)
            aw.current?.set(
                clamp((award - AWARD_SHOWS) / (1 - AWARD_SHOWS)) * (1 - clamp((end - 0.058) / 0.152)),
                clamp((f - shown) / (AWARD_TURNED - shown)),
                clamp(end / 0.202)
            )
            if (scrubs.current) seek(Math.min(duration - 0.001, Math.max(0, t)))
            // the bar on screen: the video covers the stage, centred
            const vw = innerWidth,
                vh = innerHeight
            const w = v.videoWidth || (src === "720" ? 720 : 1920),
                h = v.videoHeight || 1080
            const s = Math.max(vw / w, vh / h)
            const edge = (vh - h * s) / 2 + edgeAt(t) * h * s
            for (const l of lines.current) {
                const k = Math.max(0, Math.min(1, (edge - l.mid) / FADE))
                const o = k.toFixed(3)
                for (const w of l.words) if (w.style.opacity !== o) w.style.opacity = o
            }
        }
        // ?t=3.2 shows one moment, still (for checking)
        const q = new URLSearchParams(location.search).get("t")
        if (q != null) {
            const t = Number(q)
            const show = () => paint((t / duration) * VIDEO_PART)
            show()
            v.addEventListener("loadeddata", show)
            v.addEventListener("scrubs", show)
            addEventListener("resize", show)
            return () => removeEventListener("resize", show)
        }
        // With a finger the scroll is followed as is (it should stay under the
        // finger); with a mouse wheel it glides after it, so its steps come out smooth.
        const touch = matchMedia("(pointer: coarse)").matches
        let max = 1
        const size = () => (max = Math.max(1, document.documentElement.scrollHeight - innerHeight))
        size()
        const target = () => Math.min(1, Math.max(0, scrollY / max))
        let shown = target(),
            raf = 0,
            last = 0
        paint(shown)
        const step = (now: number) => {
            raf = 0
            const want = target()
            const dt = last ? Math.min(64, now - last) : 16.7
            last = now
            shown += (want - shown) * (1 - Math.pow(1 - 0.2, dt / 16.7))
            if (Math.abs(want - shown) < 0.00005) shown = want
            paint(shown)
            if (shown !== want) raf = requestAnimationFrame(step)
            else last = 0
        }
        const go = () => (touch ? ((shown = target()), paint(shown)) : raf || (raf = requestAnimationFrame(step)))
        const resize = () => (size(), measure(), paint(shown))
        const reloaded = () => paint(shown)
        v.addEventListener("loadeddata", reloaded)
        v.addEventListener("scrubs", reloaded)
        // (after the moment it played for iOS: back where the scroll is)
        v.addEventListener("pause", reloaded)
        addEventListener("scroll", go, { passive: true })
        addEventListener("resize", resize)
        // the page change out of here takes the scene on screen as its section
        const st = stage.current!
        Sections.set([st])
        // leaving: the page change takes the header over
        Sections.leave = release
        return () => {
            cancelAnimationFrame(raf)
            removeEventListener("scroll", go)
            removeEventListener("resize", resize)
            v.removeEventListener("seeked", onSeeked)
            v.removeEventListener("scrubs", reloaded)
            v.removeEventListener("loadeddata", reloaded)
            v.removeEventListener("pause", reloaded)
            v.removeEventListener("loadedmetadata", onMeta)
            Sections.clear([st])
            if (Sections.leave) Sections.leave = null
            release()
        }
    }, [])

    return (
        <div ref={root} className="ab av">
            <div ref={stage} className="ab-stage" data-nto-scene="">
                <video
                    ref={video}
                    className="ab-photo av-video"
                    poster={`/about/dark-poster-${src}.jpg`}
                    muted
                    playsInline
                    preload="auto"
                    disablePictureInPicture
                    aria-hidden="true"
                >
                    {/* every frame a keyframe: it scrubs at once, either way */}
                    <source src={`/about/dark-${src}.mp4`} type="video/mp4" />
                    <source src={`/about/dark-${src}.webm`} type="video/webm" />
                </video>
                <Constellation api={cn} />
                <AwardVideo api={aw} />
                <div ref={intro} className="ab-intro">
                    <IntroText text={ABOUT.intro} className="ab-intro-d" />
                    <IntroText text={ABOUT.introPhone} className="ab-intro-m" />
                </div>
            </div>
        </div>
    )
}
